import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useLayoutEffect, useRef, } from 'react';
/*
 * `HTMLElement` y no `HTMLButtonElement`: una fila puede ser un enlace —ver
 * `href` en `SidebarItem`— y un ancla no tiene `.disabled`. Con el tipo
 * anterior TypeScript dejaba leerlo igual y en el navegador salía `undefined`,
 * que por casualidad se comporta bien; el día que alguien invirtiera la
 * condición, dejaría de hacerlo sin avisar.
 */
function visibleTreeItems(root) {
    return Array.from(root.querySelectorAll('[role="treeitem"]'))
        .filter((item) => (!item.hasAttribute('disabled')
        && item.offsetParent !== null
        && !item.closest('[aria-hidden="true"]')));
}
function SidebarChevron() {
    return (_jsx("svg", { viewBox: "0 0 20 20", fill: "none", "aria-hidden": "true", children: _jsx("path", { d: "m7.5 5 5 5-5 5", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round" }) }));
}
export function SidebarTree({ children, label, className }) {
    const treeRef = useRef(null);
    function handleKeyDown(event) {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')
            return;
        const target = event.target.closest('[role="treeitem"]');
        const root = treeRef.current;
        if (!target || !root)
            return;
        const items = visibleTreeItems(root);
        const currentIndex = items.indexOf(target);
        if (currentIndex < 0)
            return;
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        const next = items[currentIndex + direction];
        if (!next)
            return;
        event.preventDefault();
        next.focus();
    }
    return (_jsx("div", { ref: treeRef, className: ['wb-sidebar-tree', className].filter(Boolean).join(' '), role: "tree", "aria-label": label, onKeyDown: handleKeyDown, children: children }));
}
export function SidebarItem({ icon, label, labelPrefix, count, badge, selected = false, disabled = false, level = 1, title, className, actions, actionsOpen = false, trailing, href, onSelect, }) {
    const inner = (_jsxs(_Fragment, { children: [_jsx("span", { className: "wb-sidebar-icon", "aria-hidden": "true", children: icon }), _jsxs("span", { className: "wb-sidebar-label", children: [labelPrefix, label] }), badge ? _jsx("span", { className: "wb-sidebar-badge", children: badge }) : null, count !== undefined ? _jsx("span", { className: "wb-sidebar-count", children: count }) : null, trailing ? _jsx("span", { className: "wb-sidebar-trailing", "aria-hidden": "true", children: trailing }) : null] }));
    const shared = {
        role: 'treeitem',
        'aria-selected': selected,
        'aria-level': level,
        className: [
            'wb-sidebar-row',
            selected ? 'is-selected' : '',
            className,
        ].filter(Boolean).join(' '),
        'data-level': level,
        title: title ?? label,
    };
    const row = href
        ? (_jsx("a", { ...shared, href: href, target: "_blank", rel: "noreferrer", onClick: onSelect, children: inner }))
        : (_jsx("button", { ...shared, type: "button", disabled: disabled, onClick: onSelect, children: inner }));
    if (!actions)
        return row;
    // El mando de acciones va fuera del botón de la fila —un botón dentro de otro
    // no es HTML válido ni se navega con el teclado—, superpuesto justo encima del
    // contador: al pasar por encima uno se apaga y el otro aparece en su sitio.
    return (_jsxs("div", { className: `wb-sidebar-row-wrap${actionsOpen ? ' has-actions-open' : ''}`, children: [row, _jsx("span", { className: "wb-sidebar-row-actions", children: actions })] }));
}
export function SidebarFolder({ icon, openIcon, label, labelPrefix, count, badge, selected = false, containsSelected = false, disabled = false, expanded, level = 1, title, className, children, onToggle, onSelect, }) {
    const branchRef = useRef(null);
    const innerRef = useRef(null);
    // Publish the content's natural height as a CSS var so the branch can animate
    // `height: 0 ↔ var(...)` between two known pixel values. That's far smoother
    // than transitioning `grid-template-rows: 0fr ↔ 1fr` (fr-interpolation forces
    // a per-frame intrinsic-size relayout and stutters). Measured even while
    // collapsed (the inner isn't height-constrained), and kept live via a
    // ResizeObserver so it stays correct when items change.
    useLayoutEffect(() => {
        const branch = branchRef.current;
        const inner = innerRef.current;
        if (!branch || !inner)
            return;
        const sync = () => branch.style.setProperty('--wb-branch-h', `${inner.scrollHeight}px`);
        sync();
        const observer = new ResizeObserver(sync);
        observer.observe(inner);
        return () => observer.disconnect();
    }, []);
    function handleFolderKeyDown(event) {
        if (event.key === 'ArrowRight' && !expanded) {
            event.preventDefault();
            onToggle();
        }
        else if (event.key === 'ArrowLeft' && expanded) {
            event.preventDefault();
            onToggle();
        }
    }
    return (_jsxs("div", { className: [
            'wb-sidebar-folder',
            containsSelected ? 'contains-selected' : '',
            className,
        ].filter(Boolean).join(' '), children: [_jsxs("div", { className: ['wb-sidebar-row', 'wb-sidebar-row--folder', selected ? 'is-selected' : ''].filter(Boolean).join(' '), "data-level": level, children: [_jsxs("button", { type: "button", role: "treeitem", "aria-selected": selected, "aria-expanded": expanded, "aria-level": level, className: "wb-sidebar-folder-main", disabled: disabled, title: title ?? label, onClick: onSelect ?? onToggle, onKeyDown: handleFolderKeyDown, children: [_jsx("span", { className: "wb-sidebar-icon", "aria-hidden": "true", children: expanded && openIcon ? openIcon : icon }), _jsxs("span", { className: "wb-sidebar-label", children: [labelPrefix, label] }), badge ? _jsx("span", { className: "wb-sidebar-badge", children: badge }) : null, count !== undefined ? _jsx("span", { className: "wb-sidebar-count", children: count }) : null] }), _jsx("button", { type: "button", className: "wb-sidebar-disclosure", disabled: disabled, tabIndex: -1, "aria-label": expanded ? `Cerrar ${label}` : `Abrir ${label}`, onClick: onToggle, children: _jsx(SidebarChevron, {}) })] }), _jsx("div", { ref: branchRef, className: "wb-sidebar-branch", "data-expanded": expanded ? 'true' : 'false', "aria-hidden": !expanded, inert: !expanded ? true : undefined, children: _jsx("div", { ref: innerRef, className: "wb-sidebar-branch-inner", role: "group", children: children }) })] }));
}
//# sourceMappingURL=SidebarNavigation.js.map